import React, { useState, useEffect } from 'react';
import {
  FaGift,
  FaSave,
  FaUsers,
  FaCalendarAlt,
  FaCrown,
  FaStop,
  FaPlay,
  FaPause,
  FaPlus,
  FaSearch,
  FaTimes,
  FaSync,
  FaCheckCircle,
  FaInfoCircle,
  FaBolt,
  FaClock,
  FaCircle,
  FaLeaf,
  FaMedal,
  FaExclamationTriangle,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import { apiHelper } from '../../utils/api';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import useConfirm from '../../hooks/useConfirm';
import './Admin.scss';

const toDateTimeInput = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  // datetime-local format: YYYY-MM-DDTHH:mm (local time for easy admin use)
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const toDateInput = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  // date format: YYYY-MM-DD (local time for easy admin use)
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const PLAN_META = {
  free: { label: 'Free', icon: <FaLeaf />, color: '#10b981', activeClass: 'is-active--free' },
  silver: { label: 'Silver', icon: <FaMedal />, color: '#64748b', activeClass: 'is-active--silver' },
  gold: { label: 'Gold', icon: <FaCrown />, color: '#f59e0b', activeClass: 'is-active--gold' },
};

const SignupPlanControl = () => {
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [offerSaving, setOfferSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [regular, setRegular] = useState({
    defaultNewUserPlan: 'free',
    freeDays: 15,
    silverDays: 30,
    goldDays: 60,
    isEnabled: true,
  });

  const [offer, setOffer] = useState({
    isActive: false,
    title: '',
    plan: 'gold',
    days: 60,
    daysMode: 'days', // 'days' = number of days | 'date' = valid up to a date
    planValidTill: '',
    maxUsers: 100,
    claimedCount: 0,
    startAt: '',
    validTill: '',
    endedReason: null,
    daysLeft: null,
    seatsLeft: null,
    startsInDays: null,
    isScheduled: false,
    isLive: false,
  });

  const [recent, setRecent] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [viaFilter, setViaFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [recentLoading, setRecentLoading] = useState(false);

  const applyConfig = (cfg) => {
    if (!cfg) return;
    setRegular({
      defaultNewUserPlan: cfg.defaultNewUserPlan || 'free',
      freeDays: cfg.freeDays ?? 15,
      silverDays: cfg.silverDays ?? 30,
      goldDays: cfg.goldDays ?? 60,
      isEnabled: cfg.isEnabled !== false,
    });
    const o = cfg.offer || {};
    const tillInput = toDateInput(o.planValidTill);
    setOffer({
      isActive: !!o.isActive,
      title: o.title || '',
      plan: o.plan || 'gold',
      days: o.days ?? 60,
      daysMode: tillInput ? 'date' : 'days',
      planValidTill: tillInput,
      maxUsers: o.maxUsers ?? 100,
      claimedCount: o.claimedCount ?? 0,
      startAt: toDateTimeInput(o.startAt),
      validTill: toDateTimeInput(o.validTill),
      endedReason: o.endedReason || null,
      daysLeft: o.daysLeft ?? null,
      seatsLeft: o.seatsLeft ?? null,
      startsInDays: o.startsInDays ?? null,
      isScheduled: !!o.isScheduled,
      isLive: o.isLive !== false && !!o.isActive && !o.isScheduled,
    });
  };

  const applyRecent = (data) => {
    setRecent(Array.isArray(data?.recent) ? data.recent : []);
    const p = data?.pagination;
    if (p) {
      setPage(p.page || 1);
      setLimit(p.limit || 10);
      setTotal(p.total || 0);
      setTotalPages(p.totalPages || 1);
    }
  };

  // Debounce search typing → actual query (resets to page 1)
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchRecent = async (opts = {}) => {
    const qPage = opts.page ?? page;
    const qSearch = opts.search !== undefined ? opts.search : search;
    const qPlan = opts.planFilter !== undefined ? opts.planFilter : planFilter;
    const qVia = opts.viaFilter !== undefined ? opts.viaFilter : viaFilter;
    const qLimit = opts.limit ?? limit;
    setRecentLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(qPage),
        limit: String(qLimit),
      });
      if (qSearch) params.set('search', qSearch);
      if (qPlan) params.set('plan', qPlan);
      if (qVia) params.set('via', qVia);
      const data = await apiHelper.get(`/admin/signup-plan?${params.toString()}`);
      if (data?.config) applyConfig(data.config);
      applyRecent(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load registers');
    } finally {
      setRecentLoading(false);
    }
  };

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: '1', limit: String(limit) });
      if (search) params.set('search', search);
      if (planFilter) params.set('plan', planFilter);
      if (viaFilter) params.set('via', viaFilter);
      const data = await apiHelper.get(`/admin/signup-plan?${params.toString()}`);
      applyConfig(data?.config);
      applyRecent(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load signup plan settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstTableLoad = React.useRef(true);
  useEffect(() => {
    // Skip the very first render — fetchConfig already loaded page 1
    if (firstTableLoad.current) {
      firstTableLoad.current = false;
      return;
    }
    fetchRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, planFilter, viaFilter, limit]);

  const handleSearchChange = (v) => setSearchInput(v);
  const handlePlanChange = (v) => { setPage(1); setPlanFilter(v); };
  const handleViaChange = (v) => { setPage(1); setViaFilter(v); };
  const handleLimitChange = (v) => { setPage(1); setLimit(Number(v) || 10); };
  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setPlanFilter('');
    setViaFilter('');
    setPage(1);
  };

  const pageNumbers = () => {
    const totalP = Math.max(1, totalPages);
    const cur = Math.min(Math.max(1, page), totalP);
    const start = Math.max(1, Math.min(cur - 2, totalP - 4));
    const end = Math.min(totalP, start + 4);
    const nums = [];
    for (let i = start; i <= end; i++) nums.push(i);
    return nums;
  };

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 4000);
  };

  const saveRegular = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await apiHelper.put('/admin/signup-plan', {
        defaultNewUserPlan: regular.defaultNewUserPlan,
        freeDays: Number(regular.freeDays),
        silverDays: Number(regular.silverDays),
        goldDays: Number(regular.goldDays),
        isEnabled: regular.isEnabled,
      });
      if (data?.config) {
        const cfg = data.config;
        setRegular({
          defaultNewUserPlan: cfg.defaultNewUserPlan || 'free',
          freeDays: cfg.freeDays ?? 15,
          silverDays: cfg.silverDays ?? 30,
          goldDays: cfg.goldDays ?? 60,
          isEnabled: cfg.isEnabled !== false,
        });
      }
      flash('Regular rule saved. New registers will get this plan.');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save regular rule');
    } finally {
      setSaving(false);
    }
  };

  const saveOffer = async (isNew, isResume = false) => {
    setOfferSaving(true);
    setError(null);
    try {
      // Client-side window validation for better UX (server validates too)
      if (offer.startAt && offer.validTill && new Date(offer.startAt) >= new Date(offer.validTill)) {
        setError('Start date/time must be before end date/time');
        setOfferSaving(false);
        return;
      }
      // Guard: seats can't go below already-claimed count on update
      if (!isNew && offer.isActive && Number(offer.maxUsers) < Number(offer.claimedCount || 0)) {
        setError(`Max users can't be below already claimed (${offer.claimedCount}). Increase seats or start a new offer to reset the count.`);
        setOfferSaving(false);
        return;
      }
      const isDateMode = offer.daysMode === 'date';
      // Date mode: plan stays valid up to the picked calendar date (end of day)
      let planValidTillIso = null;
      if (isDateMode) {
        if (!offer.planValidTill) {
          setError('Pick a valid-up-to date for the offer plan, or switch back to days mode.');
          setOfferSaving(false);
          return;
        }
        const till = new Date(`${offer.planValidTill}T23:59:00`);
        if (Number.isNaN(till.getTime())) {
          setError('Pick a valid-up-to date for the offer plan.');
          setOfferSaving(false);
          return;
        }
        if (till.getTime() <= Date.now()) {
          setError('Valid-up-to date must be in the future.');
          setOfferSaving(false);
          return;
        }
        planValidTillIso = till.toISOString();
      }
      const payload = {
        ...(isNew ? { action: 'reset' } : isResume ? { action: 'resume' } : {}),
        isActive: true,
        title: offer.title,
        plan: offer.plan,
        days: Number(offer.days),
        planValidTill: isDateMode ? planValidTillIso : null,
        maxUsers: Number(offer.maxUsers),
        startAt: offer.startAt ? new Date(offer.startAt).toISOString() : null,
        validTill: offer.validTill ? new Date(offer.validTill).toISOString() : null,
      };
      const data = await apiHelper.put('/admin/signup-plan/offer', payload);
      if (data?.config?.offer) {
        const o = data.config.offer;
        const tillInput = toDateInput(o.planValidTill);
        setOffer({
          isActive: !!o.isActive,
          title: o.title || '',
          plan: o.plan || 'gold',
          days: o.days ?? 60,
          daysMode: tillInput ? 'date' : 'days',
          planValidTill: tillInput,
          maxUsers: o.maxUsers ?? 100,
          claimedCount: o.claimedCount ?? 0,
          startAt: toDateTimeInput(o.startAt),
          validTill: toDateTimeInput(o.validTill),
          endedReason: o.endedReason || null,
          daysLeft: o.daysLeft ?? null,
          seatsLeft: o.seatsLeft ?? null,
          startsInDays: o.startsInDays ?? null,
          isScheduled: !!o.isScheduled,
          isLive: o.isLive !== false && !!o.isActive && !o.isScheduled,
        });
      }
      flash(isNew ? 'New offer started!' : isResume ? 'Offer resumed — same seats and dates.' : 'Offer updated!');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save offer');
    } finally {
      setOfferSaving(false);
    }
  };

  const stopOffer = async () => {
    const confirmed = await confirm(
      'Stop this offer? New users will get the Regular rule after this.',
      { title: 'Stop Offer', confirmText: 'Stop Offer', cancelText: 'Keep Running' }
    );
    if (!confirmed) return;
    setOfferSaving(true);
    try {
      const data = await apiHelper.put('/admin/signup-plan/offer', { action: 'stop' });
      if (data?.config?.offer) {
        const o = data.config.offer;
        setOffer((prev) => ({
          ...prev,
          isActive: !!o.isActive,
          endedReason: o.endedReason || 'stopped',
          daysLeft: o.daysLeft ?? null,
          seatsLeft: o.seatsLeft ?? null,
          claimedCount: o.claimedCount ?? prev.claimedCount,
        }));
      }
      flash('Offer stopped. Regular rule is active now.');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to stop offer');
    } finally {
      setOfferSaving(false);
    }
  };

  const pauseOffer = async () => {
    const confirmed = await confirm(
      'Pause this offer? New users will get the Regular rule until you resume. Seats and dates are kept.',
      { title: 'Pause Offer', confirmText: 'Pause', cancelText: 'Keep Running' }
    );
    if (!confirmed) return;
    setOfferSaving(true);
    try {
      const data = await apiHelper.put('/admin/signup-plan/offer', { action: 'pause' });
      if (data?.config?.offer) {
        const o = data.config.offer;
        setOffer((prev) => ({
          ...prev,
          isActive: !!o.isActive,
          endedReason: o.endedReason || 'paused',
          daysLeft: o.daysLeft ?? null,
          seatsLeft: o.seatsLeft ?? null,
          claimedCount: o.claimedCount ?? prev.claimedCount,
        }));
      }
      flash('Offer paused. Regular rule applies until you resume.');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to pause offer');
    } finally {
      setOfferSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-plan-settings">
        <div className="admin-loading">
          <div className="admin-loading__spinner" />
          <p>Loading signup plan control...</p>
        </div>
      </div>
    );
  }

  const isPaused = !offer.isActive && offer.endedReason === 'paused';
  const offerEnded = !offer.isActive && !!offer.endedReason && !isPaused;
  const isScheduled = !!offer.isActive && !!offer.isScheduled;
  const isLive = !!offer.isActive && !isScheduled;
  const progress = offer.maxUsers > 0
    ? Math.min(100, Math.round(((offer.claimedCount || 0) / offer.maxUsers) * 100))
    : 0;
  const statusLabel = isLive
    ? '● LIVE'
    : isScheduled
      ? `◷ SCHEDULED${offer.startsInDays ? ` — starts in ${offer.startsInDays}d` : ''}`
      : isPaused
        ? '○ Paused by admin'
        : offer.endedReason === 'date-over'
          ? '○ Expired — time over'
          : offer.endedReason === 'limit-over'
            ? '○ Exhausted — 100% claimed'
            : offer.endedReason === 'stopped'
              ? '○ Stopped by admin'
              : '○ Inactive';
  const statusLabelClean = statusLabel.replace(/^[●◷○]\s*/, '');

  const previewPlan = isLive ? offer.plan : regular.defaultNewUserPlan;
  const previewDays = isLive
    ? offer.days
    : regular.defaultNewUserPlan === 'gold'
      ? regular.goldDays
      : regular.defaultNewUserPlan === 'silver'
        ? regular.silverDays
        : regular.freeDays;
  // Date mode: gifted plan lasts up to a fixed date instead of N days
  const previewTill = isLive && offer.daysMode === 'date' && offer.planValidTill
    ? formatDate(offer.planValidTill)
    : null;

  return (
    <div className="admin-plan-settings">
      <div className="admin-page-header" style={{ flexWrap: 'wrap', rowGap: '0.75rem' }}>
        <div className="admin-page-header__left">
          <h2>
            <span className="spc-title-badge"><FaGift /></span>
            Signup Plan Control
          </h2>
          <p>
            Control what plan + days every <b>NEW</b> register gets. Existing users are never changed.
          </p>
        </div>
        <div className="admin-page-header__right" style={{ flexShrink: 0 }}>
          <span className={`spc-header-status spc-header-status--${isLive ? 'live' : isScheduled ? 'scheduled' : isPaused ? 'paused' : 'idle'}`}>
            {isLive ? <FaBolt /> : isScheduled ? <FaClock /> : isPaused ? <FaPause /> : <FaCircle />}
            {isLive ? 'Offer LIVE' : isScheduled ? 'Scheduled' : isPaused ? 'Paused' : 'Regular rule'}
          </span>
        </div>
      </div>

      {success && (
        <div className="admin-success-message">
          <FaCheckCircle style={{ flexShrink: 0 }} />
          {success}
        </div>
      )}
      {error && (
        <div className="admin-error-banner">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <FaExclamationTriangle style={{ flexShrink: 0 }} />
            {error}
          </span>
        </div>
      )}

      {/* Live preview banner */}
      <div className="spc-banner">
        <div className="spc-banner__icon"><FaCrown /></div>
        <div className="spc-banner__body">
          <div className="spc-banner__chip">
            {isLive ? (
              <><FaBolt /> Offer LIVE</>
            ) : isScheduled ? (
              <><FaClock /> Scheduled</>
            ) : (
              <><span className="spc-live-dot" /> Regular rule</>
            )}
          </div>
          <h3>
            New users get <span>{String(previewPlan).toUpperCase()}</span>{' '}
            {previewTill ? <>till <span>{previewTill}</span></> : <>for <span>{previewDays} days</span></>}
          </h3>
          <p>
            {isLive
              ? `${offer.title || 'Limited offer'} — ${offer.seatsLeft ?? '-'} seats left${offer.daysLeft !== null ? `, ${offer.daysLeft} days left` : ''}.`
              : isScheduled
                ? `${offer.title || 'Upcoming offer'} — starts ${formatDateTime(offer.startAt)}, ends ${formatDateTime(offer.validTill)}. Regular rule applies till then.`
                : 'No live offer right now — Regular rule is in charge for every new register.'}
          </p>
        </div>
      </div>

      <div className="spc-grid">
        {/* Regular rule card */}
        <section className="spc-card">
          <header className="spc-card__header">
            <div className="spc-card__icon spc-card__icon--regular"><FaUsers /></div>
            <div className="spc-card__title">
              <h3>Regular Rule</h3>
              <p>Always active. Applies when no offer is running.</p>
            </div>
          </header>

          <div className="spc-card__body spc-card__body--regular">
            <div className="spc-regular-head">
              <span>Plan (left)</span>
              <span>Days (right)</span>
            </div>
            <div className="spc-regular-grid">
              {[
                { key: 'free', daysKey: 'freeDays', daysLabel: 'Free days', daysIcon: <FaLeaf /> },
                { key: 'silver', daysKey: 'silverDays', daysLabel: 'Silver days', daysIcon: <FaMedal /> },
                { key: 'gold', daysKey: 'goldDays', daysLabel: 'Gold days', daysIcon: <FaCrown /> },
              ].map((row) => {
                const meta = PLAN_META[row.key];
                const active = regular.defaultNewUserPlan === row.key;
                return (
                  <div className="spc-regular-row" key={row.key}>
                    <button
                      type="button"
                      className={`spc-plan-opt ${active ? meta.activeClass : ''}`}
                      onClick={() => setRegular((r) => ({ ...r, defaultNewUserPlan: row.key }))}
                    >
                      <span className="spc-plan-opt__icon">{meta.icon}</span>
                      <span className="spc-plan-opt__name">{meta.label}</span>
                      {active && <span className="spc-plan-opt__check"><FaCheckCircle /></span>}
                    </button>
                    <div className="spc-field">
                      <label>{row.daysIcon}{row.daysLabel}</label>
                      <div className="spc-input-group">
                        <input
                          type="number" min={1} max={3650}
                          value={regular[row.daysKey]}
                          onChange={(e) => setRegular((r) => ({ ...r, [row.daysKey]: e.target.value }))}
                        />
                        <span className="spc-input-group__suffix">Days</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <footer className="spc-card__footer">
            <button className="spc-btn spc-btn--primary spc-btn--block" onClick={saveRegular} disabled={saving}>
              <FaSave />{saving ? 'Saving…' : 'Save Regular Rule'}
            </button>
            <div className="spc-card__note">
              <FaInfoCircle />
              <span>
                Example: Free = 15, Silver = 30, Gold = 60. A new register automatically gets {String(regular.defaultNewUserPlan).toUpperCase()} with its configured days.
              </span>
            </div>
          </footer>
        </section>

        {/* Limited offer card */}
        <section className={`spc-card${offerEnded ? ' spc-card--faded' : ''}`}>
          <header className="spc-card__header">
            <div className="spc-card__icon spc-card__icon--offer"><FaGift /></div>
            <div className="spc-card__title">
              <h3>Limited Offer</h3>
              <p>e.g. First 100 get Gold FREE — set times to schedule it</p>
            </div>
            <span className={`spc-status spc-status--${isLive ? 'live' : isScheduled ? 'scheduled' : isPaused ? 'paused' : offerEnded ? 'ended' : 'inactive'}`}>
              {isLive ? <FaBolt /> : isScheduled ? <FaClock /> : isPaused ? <FaPause /> : offerEnded ? <FaExclamationTriangle /> : <FaCircle />}
              {statusLabelClean}
            </span>
          </header>

          {offerEnded && (
            <div className="spc-alert spc-alert--ended">
              <FaExclamationTriangle />
              <span>
                Offer ended (<b>{statusLabelClean}</b>). Regular rule is active now. Edit the fields below and start a new offer to run again.
              </span>
            </div>
          )}
          {isPaused && (
            <div className="spc-alert spc-alert--paused">
              <FaPause />
              <span>
                Paused — new users get the Regular rule for now. <b>{offer.claimedCount || 0} / {offer.maxUsers || 0}</b> seats kept. Resume to continue the same run.
              </span>
            </div>
          )}
          {isScheduled && (
            <div className="spc-alert spc-alert--scheduled">
              <FaClock />
              <span>
                Scheduled — goes LIVE on <b>{formatDateTime(offer.startAt)}</b>, ends <b>{formatDateTime(offer.validTill)}</b>. New users get Regular till then.
              </span>
            </div>
          )}

          <fieldset className="spc-fieldset" disabled={offerSaving}>
            <div className="spc-card__body">
              <div className="spc-field spc-field--full">
                <label>Offer title (for ad)</label>
                <input
                  type="text"
                  placeholder="First 100 users get Gold FREE"
                  value={offer.title}
                  onChange={(e) => setOffer((o) => ({ ...o, title: e.target.value }))}
                />
                <span className="spc-hint">Shown to new users when the offer is live.</span>
              </div>

              <div className="spc-offer-grid spc-offer-grid--trio">
                <div className="spc-field">
                  <label>Offer plan</label>
                  <CustomSelect
                    value={offer.plan}
                    disabled={offerSaving}
                    onChange={(val) => setOffer((o) => ({ ...o, plan: val }))}
                    options={[
                      { value: 'gold', label: 'Gold' },
                      { value: 'silver', label: 'Silver' },
                    ]}
                  />
                </div>

                <div className="spc-field">
                  <label>
                    Offer days
                    <button
                      type="button"
                      className={`spc-mode-toggle${offer.daysMode === 'date' ? ' is-date' : ''}`}
                      aria-pressed={offer.daysMode === 'date'}
                      aria-label={offer.daysMode === 'date' ? 'Switch offer days to number of days' : 'Switch offer days to valid-up-to date'}
                      title={offer.daysMode === 'date' ? 'Date mode — click for days mode' : 'Days mode — click for date mode'}
                      disabled={offerSaving}
                      onClick={() => setOffer((o) => ({ ...o, daysMode: o.daysMode === 'date' ? 'days' : 'date' }))}
                    >
                      <span className="spc-mode-toggle__knob" />
                    </button>
                  </label>
                  {offer.daysMode === 'date' ? (
                    <input
                      type="date"
                      value={offer.planValidTill}
                      onChange={(e) => setOffer((o) => ({ ...o, planValidTill: e.target.value }))}
                    />
                  ) : (
                    <div className="spc-input-group">
                      <input
                        type="number" min={1} max={3650}
                        value={offer.days}
                        onChange={(e) => setOffer((o) => ({ ...o, days: e.target.value }))}
                      />
                      <span className="spc-input-group__suffix">Days</span>
                    </div>
                  )}
                  <span className="spc-hint">
                    {offer.daysMode === 'date'
                      ? 'Plan stays valid up to this date.'
                      : 'Plan lasts this many days.'}
                  </span>
                </div>

                <div className="spc-field">
                  <label>Max users (seats)</label>
                  <div className="spc-input-group">
                    <input
                      type="number" min={1} max={100000}
                      value={offer.maxUsers}
                      onChange={(e) => setOffer((o) => ({ ...o, maxUsers: e.target.value }))}
                    />
                    <span className="spc-input-group__suffix">Users</span>
                  </div>
                </div>
              </div>

              <div className="spc-offer-grid spc-offer-grid--duo">
                <div className="spc-field">
                  <label><FaCalendarAlt />Start date + time</label>
                  <input
                    type="datetime-local"
                    value={offer.startAt}
                    onChange={(e) => setOffer((o) => ({ ...o, startAt: e.target.value }))}
                  />
                  <span className="spc-hint">Empty = start now · future = scheduled</span>
                </div>

                <div className="spc-field">
                  <label><FaCalendarAlt />End date + time</label>
                  <input
                    type="datetime-local"
                    value={offer.validTill}
                    onChange={(e) => setOffer((o) => ({ ...o, validTill: e.target.value }))}
                  />
                  <span className="spc-hint">Empty = no end date · auto-off at this time</span>
                </div>
              </div>

              {/* Progress */}
              <div className="spc-progress">
                <div className="spc-progress__top">
                  <span>{offer.claimedCount || 0} / {offer.maxUsers || 0} claimed</span>
                  <span className={`spc-progress__badge ${isLive ? 'spc-progress__badge--live' : 'spc-progress__badge--idle'}`}>
                    {isLive ? <><FaBolt />Live</> : isPaused ? <><FaPause />Paused</> : offer.daysLeft !== null ? `${offer.daysLeft} days left` : 'Not running'}
                  </span>
                </div>
                <div className="spc-progress__track">
                  <div
                    className={`spc-progress__bar ${isLive ? 'spc-progress__bar--live' : 'spc-progress__bar--idle'}`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>

            <footer className="spc-card__footer">
              {offer.isActive ? (
                <div className="spc-offer-actions">
                  <button className="spc-btn spc-btn--primary" onClick={() => saveOffer(false)} disabled={offerSaving}>
                    <FaSave />{offerSaving ? 'Saving…' : 'Update Offer'}
                  </button>
                  <button className="spc-btn spc-btn--ghost" onClick={pauseOffer} disabled={offerSaving}>
                    <FaPause />{offerSaving ? 'Pausing…' : 'Pause'}
                  </button>
                  <button className="spc-btn spc-btn--danger-soft" onClick={stopOffer} disabled={offerSaving}>
                    <FaStop />Stop Offer
                  </button>
                </div>
              ) : isPaused ? (
                <div className="spc-offer-actions">
                  <button className="spc-btn spc-btn--success" onClick={() => saveOffer(false, true)} disabled={offerSaving}>
                    <FaPlay />{offerSaving ? 'Resuming…' : 'Resume Offer'}
                  </button>
                  <button className="spc-btn spc-btn--danger-soft" onClick={stopOffer} disabled={offerSaving}>
                    <FaStop />Stop Offer
                  </button>
                </div>
              ) : (
                <button
                  className="spc-btn spc-btn--success spc-btn--block"
                  onClick={() => saveOffer(true)}
                  disabled={offerSaving}
                >
                  {offerEnded
                    ? <><FaPlus />{offerSaving ? 'Starting…' : 'Start New Offer (resets count)'}</>
                    : <><FaPlay />{offerSaving ? 'Starting…' : 'Start Offer'}</>}
                </button>
              )}
            </footer>
          </fieldset>
        </section>
      </div>

      {/* Recent signups */}
      <section className="spc-card" style={{ marginBottom: 32 }}>
        <header className="spc-card__header">
          <div className="spc-card__icon spc-card__icon--table"><FaUsers /></div>
          <div className="spc-card__title">
            <h3>Recent Registers — what they got</h3>
            <p>Verified users only (same as Dashboard). Search anything + pages.</p>
          </div>
          <button className="spc-btn spc-btn--ghost spc-btn--sm" onClick={() => fetchRecent()}>
            <FaSync />Refresh
          </button>
        </header>

        <div className="spc-card__body">
          {/* Search + filter toolbar */}
          <div className="spc-toolbar">
            <div className="spc-search">
              <FaSearch size={13} />
              <input
                type="text"
                placeholder="Search name, email, plan, via, date…"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') handleSearchChange(''); }}
              />
              {searchInput ? (
                <button className="spc-search__clear" onClick={() => handleSearchChange('')}>
                  <FaTimes size={10} />Clear
                </button>
              ) : (
                <span className="spc-search__hint">type to filter</span>
              )}
            </div>

            <div className="spc-filter">
              <CustomSelect
                value={planFilter}
                onChange={(val) => handlePlanChange(val)}
                className={planFilter ? 'is-active' : ''}
                options={[
                  { value: '', label: 'All plans' },
                  { value: 'free', label: 'Free' },
                  { value: 'silver', label: 'Silver' },
                  { value: 'gold', label: 'Gold' },
                ]}
              />
            </div>

            <div className="spc-filter">
              <CustomSelect
                value={viaFilter}
                onChange={(val) => handleViaChange(val)}
                className={viaFilter ? 'is-active' : ''}
                options={[
                  { value: '', label: 'All sources' },
                  { value: 'offer', label: 'Offer' },
                  { value: 'regular', label: 'Regular' },
                  { value: 'legacy', label: 'Legacy' },
                ]}
              />
            </div>

            <div className="spc-filter">
              <CustomSelect
                value={limit}
                onChange={(val) => handleLimitChange(val)}
                options={[
                  { value: 10, label: '10' },
                  { value: 20, label: '20' },
                  { value: 50, label: '50' },
                ]}
              />
            </div>

            <div className="spc-toolbar__right">
              {(searchInput || search || planFilter || viaFilter) && (
                <button className="spc-btn spc-btn--danger-soft spc-btn--sm" onClick={clearFilters}>
                  <FaTimes />Clear all
                </button>
              )}
            </div>
          </div>

          {/* Active filter pills + count */}
          <div className="spc-meta">
            <span className="spc-meta__count">
              {recentLoading ? 'Searching…' : total === 0 ? 'No results' : `${total} result${total === 1 ? '' : 's'} found`}
            </span>
            {search && (
              <span className="spc-meta__pill spc-meta__pill--search">
                “{search}”
                <button onClick={() => handleSearchChange('')} aria-label="Clear search"><FaTimes /></button>
              </span>
            )}
            {planFilter && (
              <span className="spc-meta__pill spc-meta__pill--plan">
                {planFilter}
                <button onClick={() => handlePlanChange('')} aria-label="Clear plan"><FaTimes /></button>
              </span>
            )}
            {viaFilter && (
              <span className="spc-meta__pill spc-meta__pill--via">
                {viaFilter}
                <button onClick={() => handleViaChange('')} aria-label="Clear source"><FaTimes /></button>
              </span>
            )}
          </div>

          <div className={`spc-table-wrap ${recentLoading ? 'is-loading' : ''}`}>
            <table className="spc-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Plan</th>
                  <th>Via</th>
                  <th>Days</th>
                  <th>Joined</th>
                  <th>Expires</th>
                  <th>Left</th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 && (
                  <tr>
                    <td colSpan={8} className="spc-table__empty">
                      {search || planFilter || viaFilter ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <FaSearch />No matches — try another search or clear filters.
                        </span>
                      ) : 'No users yet.'}
                    </td>
                  </tr>
                )}
                {recent.map((u, idx) => (
                  <tr key={u._id}>
                    <td className="spc-sno">{(page - 1) * limit + idx + 1}</td>
                    <td>
                      <div className="spc-user-cell">
                        <div className="name">{u.name}</div>
                        <div className="email">{u.email}</div>
                      </div>
                    </td>
                    <td><span className={`spc-chip spc-chip--${u.plan || 'free'}`}>{u.plan}</span></td>
                    <td><span className={`spc-chip spc-chip--${u.assignedVia || 'legacy'}`}>{u.assignedVia || 'legacy'}</span></td>
                    <td className="spc-cell spc-cell--days">{u.initialPlanDays ?? (u.plan === 'gold' || u.plan === 'silver' ? 365 : 15)}{(u.planExtraDays || 0) !== 0 ? ` ${u.planExtraDays > 0 ? '+' : ''}${u.planExtraDays}` : ''}</td>
                    <td className="spc-cell spc-cell--date">{u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                    <td className="spc-cell spc-cell--date">{u.subscription?.expiresAt ? new Date(u.subscription.expiresAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                    <td className="spc-cell spc-cell--left">{u.subscription?.daysRemaining ?? '-'}d{u.subscription?.isExpired ? ' (expired)' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div className="spc-pagination">
            <span className="spc-pagination__info">
              {total === 0
                ? '0 results'
                : <>Showing <b>{Math.min(total, (page - 1) * limit + 1)}–{Math.min(total, page * limit)}</b> of <b>{total}</b></>}
              {recentLoading ? ' • loading…' : ''}
            </span>
            <div className="spc-pagination__nav">
              <button
                className="spc-btn spc-btn--ghost spc-btn--sm"
                disabled={page <= 1 || recentLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <FaChevronLeft style={{ fontSize: 10 }} />Prev
              </button>
              {pageNumbers().map((n) => (
                <button
                  key={n}
                  className={`spc-btn ${n === page ? 'spc-btn--primary' : 'spc-btn--ghost'} spc-btn--sm`}
                  disabled={recentLoading}
                  onClick={() => setPage(n)}
                  style={{ minWidth: 36, paddingLeft: 12, paddingRight: 12 }}
                >
                  {n}
                </button>
              ))}
              <button
                className="spc-btn spc-btn--ghost spc-btn--sm"
                disabled={page >= totalPages || recentLoading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next<FaChevronRight style={{ fontSize: 10 }} />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SignupPlanControl;